import React, {
  createContext,
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { INotCompletedClients, INotCompletedContext } from './interfases';
import { createNotCompletedStorage } from './notCompletedStorage';

const NotCompletedContext = createContext<INotCompletedContext | null>(null);

export const useNotCompleted = (): INotCompletedContext => {
  const context = useContext(NotCompletedContext);
  if (!context) {
    throw new Error(
      'useNotCompleted must be used within a NotCompletedProvider'
    );
  }
  return context;
};

export const NotCompletedProvider: FC<{ sandbox?: boolean }> = ({
  children,
  sandbox = false,
}) => {
  const [notCompletedRegisters, setNotCompletedRegisters] = useState<
    INotCompletedClients[]
  >([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const storage = useMemo(
    () => createNotCompletedStorage(window.localStorage, sandbox),
    [sandbox]
  );

  const updateNotCompletedRegisters = (): void => {
    const savedData = storage.load();
    const currentTime = Date.now();

    const filteredData = savedData.filter((item: INotCompletedClients) => {
      return currentTime - item.createdAt < 5 * 60 * 1000;
    });
    setNotCompletedRegisters(filteredData);

    storage.save(filteredData);

    if (filteredData.length === 0 && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const addNewData = (newData: INotCompletedClients): void => {
    const savedData = storage.load();
    savedData.push(newData);
    storage.save(savedData);
    updateNotCompletedRegisters();

    if (!intervalRef.current) {
      intervalRef.current = setInterval(updateNotCompletedRegisters, 500);
    }
  };

  const clearNotCompletedRegisters = (): void => {
    storage.clear();
    setNotCompletedRegisters([]);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const removeClientById = (verifyClientId: number): void => {
    setTimeout(() => {
      const savedData = storage.load();
      const filteredData = savedData.filter(
        (item: INotCompletedClients) => item.verifyClientId !== verifyClientId
      );

      storage.save(filteredData);
      setNotCompletedRegisters(filteredData);
    }, 0);
  };

  const markClientAsVerified = (verifyClientId: number): void => {
    setNotCompletedRegisters((prev) =>
      prev.map((client) =>
        client.verifyClientId === verifyClientId
          ? { ...client, isVerified: true }
          : client
      )
    );
  };

  useEffect(() => {
    updateNotCompletedRegisters();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return (
    <NotCompletedContext.Provider
      value={{
        notCompletedRegisters,
        addNewData,
        clearNotCompletedRegisters,
        removeClientById,
        markClientAsVerified,
      }}
    >
      {children}
    </NotCompletedContext.Provider>
  );
};
